
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Users, BookOpen, BadgeCheck } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/integrations/supabase/client";

const AdminDashboardPage: React.FC = () => {
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getUserRole = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData?.session?.user) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', sessionData.session.user.id)
          .single();
          
        setUserRole(profileData?.role || null);
      }
      setIsLoading(false);
    };
    
    getUserRole();
  }, []);

  if (isLoading) {
    return (
      <AdminLayout title="Admin Dashboard">
        <div className="flex justify-center items-center h-64">
          <div className="h-12 w-12 border-4 border-t-primary border-r-primary/30 border-b-primary/30 border-l-primary/30 rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  const isAdmin = userRole === 'admin';

  return (
    <AdminLayout title="Admin Dashboard">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* User Management - Admin only */}
        {isAdmin && (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-techne-purple" />
                  <CardTitle>User Management</CardTitle>
                </div>
                <span className="text-2xl font-semibold text-techne-purple">👥</span>
              </div>
              <CardDescription>Manage system users and their roles</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="mb-4">Manage user accounts, edit profiles, and assign admin privileges.</p>
              <Link 
                to="/admin/users" 
                className="inline-block px-4 py-2 bg-techne-purple text-white rounded-md hover:bg-techne-deepPurple"
              >
                Manage Users
              </Link>
            </CardContent>
          </Card>
        )}
        
        {/* Course Management - Both admin and instructor */}
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-techne-purple" />
                <CardTitle>Course Management</CardTitle>
              </div>
              <span className="text-2xl font-semibold text-techne-purple">📚</span>
            </div>
            <CardDescription>Manage courses and lessons</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="mb-4">Create and edit courses, lessons, and manage course content.</p>
            <Link 
              to="/admin/courses" 
              className="inline-block px-4 py-2 bg-techne-purple text-white rounded-md hover:bg-techne-deepPurple"
            >
              Manage Courses
            </Link>
          </CardContent>
        </Card>
        
        {/* Certificate Management - Admin only */}
        {isAdmin && (
          <Card className="hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BadgeCheck className="h-5 w-5 text-techne-purple" />
                  <CardTitle>Certificate Management</CardTitle>
                </div>
                <span className="text-2xl font-semibold text-techne-purple">🏆</span>
              </div>
              <CardDescription>Manage certificate templates and issuance</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="mb-4">Create certificate templates and issue certificates to users.</p>
              <Link 
                to="/admin/certificates" 
                className="inline-block px-4 py-2 bg-techne-purple text-white rounded-md hover:bg-techne-deepPurple"
              >
                Manage Certificates
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
